/**
 * Abstract Requirements with Variant Choices
 * Maps regulatory requirements to specific equipment implementations
 */

export const ABSTRACT_REQUIREMENTS = {
  // Universal MOB Recovery Equipment (all seagoing vessels)
  'lsa.recovery.equipment': {
    id: 'lsa.recovery.equipment',
    name: 'MOB Recovery Equipment',
    description: 'Efficient means to enable recovery of persons from water',
    domain: 'lsa',
    category: 'Life-Saving Appliances',
    mandatory: true,
    qtyRequired: 1,
    variants: [
      {
        id: 'universal_baseline',
        name: 'Universal MOB Recovery Equipment',
        description: 'Basic MOB recovery system for all seagoing vessels',
        vesselConfigKey: 'operatingStatus',
        vesselConfigValue: 'seagoing'
      }
    ]
  },

  // Pilot Boat - Buoyant Lifelines
  'lsa.pilot.lifelines': {
    id: 'lsa.pilot.lifelines',
    name: 'Buoyant Lifelines (Pilot Boat)',
    description: 'At least 2 buoyant lifelines not less than 18 metres',
    domain: 'lsa',
    category: 'Life-Saving Appliances',
    mandatory: true,
    qtyRequired: 2,
    reference: 'WBC3 Table 27.2.3 14.7',
    variants: [
      {
        id: 'pilot_lifelines',
        name: 'Buoyant Lifelines',
        description: '2 x 18m lines with quoit for pilot boats',
        vesselConfigKey: 'mobEquipment',
        vesselConfigValue: 'lsa.pilot.lifelines'
      }
    ]
  },

  // Pilot Boat - Enhanced Recovery Equipment
  'lsa.recovery.pilot.equipment': {
    id: 'lsa.recovery.pilot.equipment',
    name: 'Pilot Recovery Equipment',
    description: 'WBC3 14.7.1: Transom steps and/or ladder or equivalent side ladder or scrambling net + propeller guard',
    domain: 'lsa',
    category: 'Life-Saving Appliances',
    mandatory: true,
    qtyRequired: 1,
    reference: 'WBC3 Table 27.2.3 Section 14.7.1',
    variants: [
      {
        id: 'transom_steps_guard',
        name: 'Transom Steps + Propeller Guard',
        description: 'Transom-mounted steps with propeller guard for pilot recovery',
        vesselConfigKey: 'mobEquipment',
        vesselConfigValue: 'lsa.recovery.pilot.transom_steps'
      },
      {
        id: 'transom_ladder_guard',
        name: 'Transom Ladder + Propeller Guard',
        description: 'Transom-mounted ladder with propeller guard for pilot recovery',
        vesselConfigKey: 'mobEquipment',
        vesselConfigValue: 'lsa.recovery.pilot.transom_ladder'
      },
      {
        id: 'side_ladder_guard',
        name: 'Side Ladder + Propeller Guard',
        description: 'Side-mounted ladder with propeller guard for pilot recovery',
        vesselConfigKey: 'mobEquipment',
        vesselConfigValue: 'lsa.recovery.pilot.side_ladder'
      },
      {
        id: 'scrambling_net_guard',
        name: 'Scrambling Net + Propeller Guard',
        description: 'Scrambling net with propeller guard for pilot recovery',
        vesselConfigKey: 'mobEquipment',
        vesselConfigValue: 'lsa.recovery.pilot.scrambling_net'
      }
    ]
  },

  // Pilot Boat - Fall Prevention Equipment (conditional)
  'lsa.mob.prevention.pilot': {
    id: 'lsa.mob.prevention.pilot',
    name: 'MOB Fall Prevention Equipment',
    description: 'Fall prevention equipment for vessels without guardrails',
    domain: 'lsa',
    category: 'Life-Saving Appliances',
    mandatory: true,
    qtyRequired: 1,
    reference: 'WBC3 22.2.3',
    variants: [
      {
        id: 'fall_prevention',
        name: 'MOB Fall Prevention Equipment',
        description: 'Fall prevention equipment for vessels without guardrails',
        vesselConfigKey: 'mobEquipment',
        vesselConfigValue: 'lsa.mob.prevention.pilot'
      }
    ]
  },

  // Police Boat - Enhanced Recovery Equipment
  'lsa.recovery.police.unconscious': {
    id: 'lsa.recovery.police.unconscious',
    name: 'Enhanced MOB Recovery (Unconscious)',
    description: 'Enhanced unconscious person recovery capability',
    domain: 'lsa',
    category: 'Life-Saving Appliances',
    mandatory: true,
    qtyRequired: 1,
    reference: 'WBC3 Annex 3 20.4.2',
    variants: [
      {
        id: 'police_recovery',
        name: 'Enhanced Police Recovery Equipment',
        description: 'Enhanced unconscious person recovery capability',
        vesselConfigKey: 'mobEquipment',
        vesselConfigValue: 'lsa.recovery.police.unconscious'
      }
    ]
  },


  // Lifejacket Type
  'lsa.lifejackets.type': {
    id: 'lsa.lifejackets.type',
    name: 'Lifejacket Type',
    description: 'Lifejackets for 100% of persons on board',
    domain: 'lsa',
    category: 'Life-Saving Appliances',
    mandatory: true,
    qtyRequired: 'pob_count', // Special: quantity based on POB
    variants: [
      {
        id: 'foam_inherent',
        name: 'Foam Lifejackets (Inherent Buoyancy)',
        description: 'Lower maintenance - affects maintenance requirements and inspection frequency',
        vesselConfigKey: 'lifejacketType',
        vesselConfigValue: 'foam'
      },
      {
        id: 'inflatable',
        name: 'Inflatable Lifejackets',
        description: 'Requires 2 additional spare lifejackets and inflation system maintenance',
        vesselConfigKey: 'lifejacketType',
        vesselConfigValue: 'inflatable'
      }
    ]
  }
};

/**
 * Get variant choices for an abstract requirement
 */
export const getVariantChoices = (abstractRequirementId) => {
  const requirement = ABSTRACT_REQUIREMENTS[abstractRequirementId];
  return requirement ? requirement.variants : [];
};

/**
 * Find which variant is currently selected based on vessel config
 */
export const getCurrentVariant = (abstractRequirementId, vesselConfig) => {
  const requirement = ABSTRACT_REQUIREMENTS[abstractRequirementId];
  if (!requirement) return null;

  for (const variant of requirement.variants) {
    if (isVariantSelected(variant, vesselConfig)) {
      return variant;
    }
  }
  
  return null;
};

/**
 * Check if a specific variant is selected in vessel config
 */
const isVariantSelected = (variant, vesselConfig) => {
  if (!variant.vesselConfigKey) return false;

  // Handle array of possible config keys (check all until one matches)
  const configKeys = Array.isArray(variant.vesselConfigKey) ? variant.vesselConfigKey : [variant.vesselConfigKey];
  
  for (const configKey of configKeys) {
    const keys = configKey.split('.');
    let value = vesselConfig;
    
    // Navigate to nested property
    for (const key of keys) {
      value = value?.[key];
    }

    // Check if this config key matches the expected value
    let matches = false;
    if (typeof variant.vesselConfigValue === 'boolean') {
      matches = value === variant.vesselConfigValue;
    } else if (typeof variant.vesselConfigValue === 'string') {
      matches = Array.isArray(value) ? value.includes(variant.vesselConfigValue) : value === variant.vesselConfigValue;
    } else if (typeof variant.vesselConfigValue === 'object') {
      // For complex objects like openFlameProtection
      matches = JSON.stringify(value) === JSON.stringify(variant.vesselConfigValue);
    }
    
    // If any config key matches, the variant is selected
    if (matches) {
      return true;
    }
  }

  return false;
};

/**
 * Get all current equipment instances for an abstract requirement
 */
export const getCurrentEquipmentInstances = (abstractRequirementId, vesselConfig) => {
  const instances = [];
  const requirement = ABSTRACT_REQUIREMENTS[abstractRequirementId];
  if (!requirement) return instances;

  for (const variant of requirement.variants) {
    if (isVariantSelected(variant, vesselConfig)) {
      instances.push({
        id: `${abstractRequirementId}_${variant.id}`,
        variantId: variant.id,
        name: variant.name,
        description: variant.description,
        qtyPresent: 1, // TODO: Could be enhanced to read quantities
        source: 'vessel_config'
      });
    }
  }

  return instances;
};

/**
 * Check if an abstract requirement is satisfied
 */
export const isRequirementSatisfied = (abstractRequirementId, vesselConfig) => {
  const instances = getCurrentEquipmentInstances(abstractRequirementId, vesselConfig);
  const requirement = ABSTRACT_REQUIREMENTS[abstractRequirementId];
  
  if (!requirement) return false;
  
  const requiredQty = requirement.qtyRequired === 'pob_count' 
    ? parseInt(vesselConfig.maxPersons) || 1 
    : requirement.qtyRequired;
    
  const totalQty = instances.reduce((sum, instance) => sum + instance.qtyPresent, 0);
  
  return totalQty >= requiredQty;
};